/**
 * Thread persistence for the Ask Pine widget.
 *
 * Chats are stored in Pine's own database, via the endpoints that already back
 * the /business_intelligence/AskPine screen. The widget and that page therefore
 * share one history — the same conversation is visible from either.
 *
 * The AI answers come from the ai-ml service (see agentClient.js). This module
 * is only the store: it never generates anything. The two are separate on
 * purpose — ai-ml has no idea who the user is, and every row here is scoped by
 * user_id, so the browser is the only place that holds both the answer and the
 * identity to file it under.
 *
 * Everything here fails soft. Losing history is annoying; breaking the chat
 * because a save failed is worse. Callers get null/[] and carry on.
 */

import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * The logged-in user's display name, or "" when unavailable.
 *
 * For greeting and phrasing only. It comes from localStorage, which the user
 * can edit, so it must never be used for a permission decision - those use the
 * server-verified id carried on the ticket.
 */
export function currentUserName() {
  const u = currentUser();
  return [u.firstname, u.lastname].filter(Boolean).join(" ").trim();
}

/** The logged-in user, as the rest of Pine reads it. */
export function currentUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

/** These endpoints sit behind authenticateUser — no token, no access. */
function authConfig(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

/** Past conversations for this user, newest first. */
export async function listSessions(token) {
  try {
    const res = await axios.get(`${API_BASE_URL}/chatgpt/get_sessions`, {
      params: { user_id: currentUser().id },
      ...authConfig(token),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    console.error("[AskPine] listSessions failed:", err?.message || err);
    return [];
  }
}

/** Start a conversation. Returns its id, or null if the call failed. */
export async function createSession(token) {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/chatgpt/create_session`,
      { user_id: currentUser().id },
      authConfig(token),
    );
    return res.data?.session_id ?? null;
  } catch (err) {
    console.error("[AskPine] createSession failed:", err?.message || err);
    return null;
  }
}

/** Messages in one conversation, oldest first. */
export async function getMessages(token, sessionId) {
  if (!sessionId) return [];
  try {
    const res = await axios.post(
      `${API_BASE_URL}/chatgpt/get_messages`,
      { session_id: sessionId, user_id: currentUser().id },
      authConfig(token),
    );
    return Array.isArray(res.data) ? res.data : res.data?.messages || [];
  } catch (err) {
    console.error("[AskPine] getMessages failed:", err?.message || err);
    return [];
  }
}

/**
 * Persist one message. Returns true on success.
 *
 * Not pine_chat: that endpoint generates a reply as well as saving, so using it
 * here would produce a second answer from a different model and store that
 * instead of ours.
 */
export async function saveMessage(token, sessionId, role, content) {
  if (!sessionId || !content) return false;
  try {
    await axios.post(
      `${API_BASE_URL}/chatgpt/save_message`,
      { session_id: sessionId, role, content },
      authConfig(token),
    );
    return true;
  } catch (err) {
    console.error("[AskPine] saveMessage failed:", err?.message || err);
    return false;
  }
}

/**
 * Fetch a short-lived permission ticket.
 *
 * The ticket is an opaque random string with no information in it — the user's
 * role is resolved server-side, by the ai-ml service asking Node what the
 * ticket is worth. Editing it here achieves nothing: an unrecognised ticket
 * resolves to no permission.
 *
 * Requested per turn rather than cached, since it expires in minutes and a
 * stale one would surface as a spurious refusal.
 */
export async function issueTicket(token) {
  if (!token) return null;
  try {
    const res = await axios.post(
      `${API_BASE_URL}/chatgpt/issue_ticket`,
      {},
      authConfig(token),
    );
    return res.data?.ticket ?? null;
  } catch (err) {
    console.error("[AskPine] issueTicket failed:", err?.message || err);
    return null;
  }
}
